import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "../../utils/notify";
import { AnimatePresence } from "framer-motion";
import { motion } from "framer-motion";
import TabNavigation from "../ui/TabNavigation";
import {
  getCompanyIncidentReportsApi,
  createEmployeeIncidentReportApi,
  getEmployeeIncidentReportByIdApi,
  getIncidentCategoriesApi,
  getIncidentSeveritiesApi,
  getIncidentAnalyticsMetricsApi,
  getIncidentSLABreachesApi,
  getAISeveritySuggestionApi,
  updateIncidentStatusApi,
  getIncidentReportLogsApi,
  getEmergencyContactsApi,
  createEmergencyContactApi,
  updateEmergencyContactApi,
  deleteEmergencyContactApi,
  getEmergencyContactLogsApi,
  getCompanyEmployeesApi,
} from "../../services/axios/AuthApis/IncidentReportApis";
import { getBgvList } from "../../services/axios/AuthApis/TeamCollabApis";
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  ChartBarIcon,
  BellIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

import DashboardComponent from "./DashboardComponent";
import AllIncidentsComponent from "./AllIncidentsComponent";
import AnalyticsSLAComponent from "./AnalyticsSLAComponent";
import HelpContactsComponent from "./HelpContactsComponent";
import PageLoader from "../ui/PageLoader";

const IncidentReporting = () => {
  const { user } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  // Get customer ID regardless of user role
  const getCustomerId = () => {
    return user?.customer_master_id || "";
   
  };
  
  // Dashboard State
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  
  // Incidents State
  const [incidents, setIncidents] = useState([]);
  const [incidentFilters, setIncidentFilters] = useState({
    status: "",
    severity: "",
    category: "",
    incident_no: "",
    search: ""
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    total_pages: 0,
    total_count: 0
  });
  
  // New Incident State
  const [showNewIncidentModal, setShowNewIncidentModal] = useState(false);
  const [newIncident, setNewIncident] = useState({
    reporter_name: user?.name || "",
    reporter_email: user?.email || "",
    reporter_phone: "",
    reporter_department: "",
    title: "",
    description: "",
    category: "",
    suggested_severity: "",
    incident_occurred_at: "",
    affected_systems: [],
    affected_assets: [],
    business_impact: "",
    data_involved: false,
    personal_data_involved: false
  });
  
  // Incident Details State
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  
  // Form Data State
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  
  // Analytics State
  const [analyticsData, setAnalyticsData] = useState(null);
  const [slaBreaches, setSlaBreaches] = useState([]);

  // Incident Logs State
  const [incidentLogs, setIncidentLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Emergency Contacts State
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [contactLogs, setContactLogs] = useState([]);
  const [loadingContactLogs, setLoadingContactLogs] = useState(false);

  // Handle ?highlight=INV00015 navigation from notification click
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incidentNo = params.get("incident_no");
    if (incidentNo) {
      setActiveTab("incidents");
      setIncidentFilters((prev) => ({ ...prev, incident_no: incidentNo, search: "" }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [location.search]);

  useEffect(() => {
    loadDashboardData();
    loadFormData();
  }, []);

  useEffect(() => {
    if (activeTab === "dashboard") {
      loadDashboardData();
    } else if (activeTab === "incidents") {
      loadIncidents();
    } else if (activeTab === "analytics") {
      loadAnalyticsData();
    } else if (activeTab === "help") {
      loadEmergencyContacts();
      loadCompanyEmployees();
    }
  }, [activeTab, incidentFilters, pagination.page, pagination.page_size]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const customerId = getCustomerId();
      const data = await getCompanyIncidentReportsApi(customerId);
      const allReports = data.results || data || [];

      const metrics = {
        total_incidents: allReports.length,
        open_incidents: allReports.filter(r => !['resolved', 'closed', 'false_alarm'].includes(r.status)).length,
        critical_incidents: allReports.filter(r => r.severity === 'critical').length,
        high_incidents: allReports.filter(r => r.severity === 'high').length,
        resolved_incidents: allReports.filter(r => r.status === 'resolved').length,
      };
      const recentCritical = allReports
        .filter(r => r.severity === 'critical' || r.severity === 'high')
        .filter(r => !['resolved', 'closed', 'false_alarm'].includes(r.status));

      setDashboardMetrics({ metrics, recent_critical_incidents: recentCritical });
    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Error loading dashboard data");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadFormData = async () => {
    try {
      const categoriesData = await getIncidentCategoriesApi();
      setCategories(categoriesData.categories || []);

      const severitiesData = await getIncidentSeveritiesApi();
      setSeverities(severitiesData.severities || []);
    } catch (error) {
      console.error("Error loading form data:", error);
    }
  };

  const loadIncidents = async () => {
    try {
      setLoading(true);
      const customerId = getCustomerId();
      
      // Build API parameters including pagination and filters
      const apiParams = {
        page: pagination.page,
        page_size: pagination.page_size,
        status: incidentFilters.status || undefined,
        severity: incidentFilters.severity || undefined,
        incident_type: incidentFilters.category || undefined,
        incident_no: incidentFilters.incident_no || undefined,
        search: incidentFilters.search || undefined
      };
      
      // Remove undefined values
      Object.keys(apiParams).forEach(key => apiParams[key] === undefined && delete apiParams[key]);
      
      const data = await getCompanyIncidentReportsApi(customerId, apiParams);
      
      // Handle both paginated and non-paginated response formats for backward compatibility
      if (data.results && data.pagination) {
        // New paginated format
        setIncidents(data.results || []);
        setPagination(prev => ({
          ...prev,
          page: data.pagination.page || 1,
          page_size: data.pagination.page_size || 10,
          total_pages: data.pagination.total_pages || 0,
          total_count: data.pagination.total_count || 0,
          has_next: data.pagination.has_next || false,
          has_previous: data.pagination.has_previous || false
        }));
      } else {
        // Legacy non-paginated format - client-side pagination
        const results = data.results || data || [];
        setIncidents(results);
        setPagination(prev => ({
          ...prev,
          total_pages: Math.ceil(results.length / prev.page_size) || 0,
          total_count: results.length,
          has_next: pagination.page < Math.ceil(results.length / pagination.page_size),
          has_previous: pagination.page > 1
        }));
      }
    } catch (error) {
      console.error("Error loading incidents:", error);
      toast.error("Error loading incidents");
    } finally {
      setLoading(false);
    }
  };

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      
      const analyticsResult = await getIncidentAnalyticsMetricsApi();
      setAnalyticsData(analyticsResult);

      const slaData = await getIncidentSLABreachesApi();
      setSlaBreaches(slaData.breached_incidents || []);
    } catch (error) {
      console.error("Error loading analytics:", error);
      toast.error("Error loading analytics data");
    } finally {
      setLoading(false);
    }
  };

  const createIncident = async () => {
    try {
      setLoading(true);
      
      let aiSeverity = null;
      if (newIncident.description && newIncident.category) {
        try {
          const aiData = await getAISeveritySuggestionApi({
            description: newIncident.description,
            category: newIncident.category
          });
          aiSeverity = aiData.suggested_severity;
        } catch (error) {
          console.error("AI severity suggestion failed:", error);
        }
      }
      
      const incidentData = {
        title: newIncident.title,
        description: newIncident.description,
        incident_type: newIncident.category || 'other',
        severity: aiSeverity || newIncident.suggested_severity || 'medium',
        reporter: user?.email || user?.firstname || 'admin',
        affected_systems: (newIncident.affected_systems || []).filter(s => s.trim()).join(', '),
      };
      
      await createEmployeeIncidentReportApi(incidentData);
      toast.success('Incident created successfully!');
      setShowNewIncidentModal(false);
      resetNewIncidentForm();
      loadDashboardData();
      if (activeTab === "incidents") {
        loadIncidents();
      }
    } catch (error) {
      console.error("Error creating incident:", error);
      toast.error("Error creating incident");
    } finally {
      setLoading(false);
    }
  };

  const loadIncidentDetails = async (incidentId) => {
    try {
      const data = await getEmployeeIncidentReportByIdApi(incidentId);
      setSelectedIncident(data);
      setShowIncidentModal(true);
      // Also load logs
      loadIncidentLogs(incidentId);
    } catch (error) {
      console.error("Error loading incident details:", error);
      toast.error("Error loading incident details");
    }
  };

  const loadIncidentLogs = async (incidentId) => {
    try {
      setLoadingLogs(true);
      const data = await getIncidentReportLogsApi(incidentId);
      setIncidentLogs(data || []);
    } catch (error) {
      console.error("Error loading incident logs:", error);
      setIncidentLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleUpdateIncidentStatus = async (incidentId, newStatus, notes) => {
    try {
      await updateIncidentStatusApi(incidentId, {
        new_status: newStatus,
        notes: notes || "",
        performed_by: user?.email || "admin",
      });
      toast.success("Status updated successfully!");
      // Refresh the incident details and logs
      const updatedData = await getEmployeeIncidentReportByIdApi(incidentId);
      setSelectedIncident(updatedData);
      loadIncidentLogs(incidentId);
      // Refresh incidents list & dashboard
      loadIncidents();
      loadDashboardData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating incident status");
      throw error;
    }
  };


  const loadEmergencyContacts = async () => {
    try {
      setLoadingContacts(true);
      const customerId = getCustomerId();
      if (!customerId) return;
      const data = await getEmergencyContactsApi(customerId);
      setEmergencyContacts(data || []);
    } catch (error) {
      console.error("Error loading emergency contacts:", error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const loadCompanyEmployees = async () => {
    try {
      const customerId = getCustomerId();
      if (!customerId) return;
      const response = await getBgvList({ customer_master_id: customerId });
      const employees = (response.results || []).map(emp => ({
        employee_id: emp.employee_id,
        name: emp.firstname || '',
        designation: '',
        department: emp.department || '',
      }));
      setCompanyEmployees(employees);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const handleCreateEmergencyContact = async (formData) => {
    try {
      const customerId = getCustomerId();
      await createEmergencyContactApi({
        ...formData,
        customer_id: customerId,
        performed_by: user?.email || "admin",
      });
      toast.success("Emergency contact added!");
      loadEmergencyContacts();
    } catch (error) {
      console.error("Error creating contact:", error);
      toast.error("Error adding emergency contact");
      throw error;
    }
  };

  const handleUpdateEmergencyContact = async (contactId, formData) => {
    try {
      await updateEmergencyContactApi(contactId, {
        ...formData,
        performed_by: user?.email || "admin",
      });
      toast.success("Emergency contact updated!");
      loadEmergencyContacts();
    } catch (error) {
      console.error("Error updating contact:", error);
      toast.error("Error updating emergency contact");
      throw error;
    }
  };

  const handleDeleteEmergencyContact = async (contactId) => {
    try {
      await deleteEmergencyContactApi(contactId, user?.email || "admin");
      toast.success("Emergency contact deleted!");
      loadEmergencyContacts();
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Error deleting emergency contact");
    }
  };

  const loadContactLogs = async (contactId) => {
    try {
      setLoadingContactLogs(true);
      const data = await getEmergencyContactLogsApi(contactId);
      setContactLogs(data || []);
    } catch (error) {
      console.error("Error loading contact logs:", error);
      setContactLogs([]);
    } finally {
      setLoadingContactLogs(false);
    }
  };

  const resetNewIncidentForm = () => {
    setNewIncident({
      reporter_name: user?.name || "",
      reporter_email: user?.email || "",
      reporter_phone: "",
      reporter_department: "",
      title: "",
      description: "",
      category: "",
      suggested_severity: "",
      incident_occurred_at: "",
      affected_systems: [],
      affected_assets: [],
      business_impact: "",
      data_involved: false,
      personal_data_involved: false
    });
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800",
      high: "bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 border-orange-200 dark:border-orange-800",
      medium: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800",
      low: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800"
    };
    return colors[severity] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600";
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "bg-blue-100 dark:bg-blue-900 text-white border-blue-200 dark:border-blue-800",
      in_progress: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800",
      resolved: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-200 dark:border-green-800",
      closed: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600",
      false_alarm: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600",
      cancelled: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-200 dark:border-red-800"
    };
    return colors[status] || "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-600";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (initialLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading Incident Reporting..." minH="min-h-screen" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-7xl mx-auto bg-white dark:bg-[#0f172a] min-h-screen">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 gap-4">
          <div className="flex items-center space-x-4">
             <div className="">
              <ExclamationTriangleIcon className="w-6 md:w-8 h-6 md:h-8 text-blue-600" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-3xl font-bold text-gray-900 dark:text-white truncate">Incident Reporting</h1>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 truncate md:truncate-none">NIST 800-61 / ISO 27035 Compliant</p>
            </div>
          </div>
          
          {/* <button
            onClick={() => setShowNewIncidentModal(true)}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Report New Incident
          </button> */}
        </div>

        {/* Navigation Tabs */}
        <TabNavigation
          tabs={[
            { id: "dashboard", name: "Dashboard", icon: ChartBarIcon },
            { id: "incidents", name: "All Incidents", icon: ClipboardDocumentListIcon },
            { id: "analytics", name: "Analytics & SLA", icon: BellIcon },
            { id: "help", name: "Help & Contacts", icon: InformationCircleIcon },
          ]}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === "dashboard" && (
          <DashboardComponent
            loading={loading}
            dashboardMetrics={dashboardMetrics}
          />
        )}

        {activeTab === "incidents" && (
          <AllIncidentsComponent
            loading={loading}
            incidents={incidents}
            incidentFilters={incidentFilters}
            setIncidentFilters={setIncidentFilters}
            pagination={pagination}
            setPagination={setPagination}
            categories={categories}
            loadIncidentDetails={loadIncidentDetails}
            selectedIncident={selectedIncident}
            showIncidentModal={showIncidentModal}
            setShowIncidentModal={setShowIncidentModal}
            setSelectedIncident={setSelectedIncident}
            getSeverityColor={getSeverityColor}
            getStatusColor={getStatusColor}
            formatDate={formatDate}
            updateIncidentStatus={handleUpdateIncidentStatus}
            incidentLogs={incidentLogs}
            loadingLogs={loadingLogs}
          />
        )}

        {activeTab === "analytics" && (
          <AnalyticsSLAComponent
            loading={loading}
            analyticsData={analyticsData}
            slaBreaches={slaBreaches}
          />
        )}

        {activeTab === "help" && (
          <HelpContactsComponent
            emergencyContacts={emergencyContacts}
            loadingContacts={loadingContacts}
            companyEmployees={companyEmployees}
            customerMasterId={getCustomerId()}
            onCreateContact={handleCreateEmergencyContact}
            onUpdateContact={handleUpdateEmergencyContact}
            onDeleteContact={handleDeleteEmergencyContact}
            contactLogs={contactLogs}
            loadingContactLogs={loadingContactLogs}
            onLoadContactLogs={loadContactLogs}
            formatDate={formatDate}
          />
        )}
      </AnimatePresence>

      {/* New Incident Modal */}
      <AnimatePresence>
        {showNewIncidentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-4 sm:p-6">
                {/* Modal Header */}
                {/* <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Report New Incident</h2>
                  <button
                    onClick={() => {setShowNewIncidentModal(false); resetNewIncidentForm();}}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div> */}

                {/* New Incident Form */}
                <form className="space-y-4 sm:space-y-6">
                  {/* Reporter Information */}
                  <div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Reporter Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Name *</label>
                        <input
                          type="text"
                          value={newIncident.reporter_name}
                          onChange={(e) => setNewIncident({...newIncident, reporter_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Email *</label>
                        <input
                          type="email"
                          value={newIncident.reporter_email}
                          onChange={(e) => setNewIncident({...newIncident, reporter_email: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Phone</label>
                        <input
                          type="tel"
                          value={newIncident.reporter_phone}
                          onChange={(e) => setNewIncident({...newIncident, reporter_phone: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Department</label>
                        <input
                          type="text"
                          value={newIncident.reporter_department}
                          onChange={(e) => setNewIncident({...newIncident, reporter_department: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          placeholder="e.g., IT, HR, Finance"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Incident Details */}
                  <div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Incident Details</h3>
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Incident Title *</label>
                        <input
                          type="text"
                          value={newIncident.title}
                          onChange={(e) => setNewIncident({...newIncident, title: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          placeholder="Brief title describing the incident"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Category *</label>
                        <select
                          value={newIncident.category}
                          onChange={(e) => setNewIncident({...newIncident, category: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          required
                        >
                          <option value="">Select incident category...</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Incident Description *</label>
                        <textarea
                          value={newIncident.description}
                          onChange={(e) => setNewIncident({...newIncident, description: e.target.value})}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          placeholder="Provide detailed information about what happened, when it occurred, and any other relevant details..."
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">When did this occur?</label>
                          <input
                            type="datetime-local"
                            value={newIncident.incident_occurred_at}
                            onChange={(e) => setNewIncident({...newIncident, incident_occurred_at: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">Business Impact</label>
                          <select
                            value={newIncident.business_impact}
                            onChange={(e) => setNewIncident({...newIncident, business_impact: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 text-sm"
                          >
                            <option value="">Select impact level...</option>
                            <option value="critical">Critical</option>
                            <option value="high">High</option>
                            <option value="medium">Medium</option>
                            <option value="low">Low</option>
                          </select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="data_involved"
                              checked={newIncident.data_involved}
                              onChange={(e) => setNewIncident({...newIncident, data_involved: e.target.checked})}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <label htmlFor="data_involved" className="ml-2 block text-xs sm:text-sm text-gray-900 dark:text-gray-300">
                              Data was involved in this incident
                            </label>
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="personal_data_involved"
                              checked={newIncident.personal_data_involved}
                              onChange={(e) => setNewIncident({...newIncident, personal_data_involved: e.target.checked})}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                            />
                            <label htmlFor="personal_data_involved" className="ml-2 block text-xs sm:text-sm text-gray-900 dark:text-gray-300">
                              Personal/sensitive data was involved
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Modal Actions */}
                  <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-4 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {setShowNewIncidentModal(false); resetNewIncidentForm();}}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={createIncident}
                      disabled={loading || !newIncident.title || !newIncident.description || !newIncident.category}
                      className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm font-medium"
                    >
                      {loading && <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />}
                      Submit Incident Report
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default IncidentReporting;
